import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, Link as RouterLink } from 'react-router-dom'

import { DeleteForeverOutlined as DeleteForeverOutlinedIcon } from '@mui/icons-material'

import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Link,
  TextField,
  Typography,
} from '@mui/material'

import {
  clearFlashAlert,
  clearFlashNotice,
  setFlashAlert,
  setFlashNotice,
} from '../ui/uiSlice'

import { selectCurrentUser, selectAuthToken, clearUserData } from './userSlice'
import { clearPagesData } from '../pages/pagesSlice'
import { clearStikisData } from '../stikis/stikisSlice'
import { clearSharesData } from '../shares/sharesSlice'

import {
  splitFieldErrors,
  fieldErrorText,
} from '../../../components/devise/deviseErrors'
import {
  deviseFetch,
  showFailedRequest,
} from '../../../components/devise/deviseRequest'
import useSubmitting from '../../../hooks/useSubmitting'

// Delete the account and everything in it (DELETE /user.json, which asks for
// the current password and answers 204). There is no undo, so the page says
// what goes before it asks.
export default function DeleteAccount() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector(selectCurrentUser)
  const authToken = useSelector(selectAuthToken)
  const [errors, setErrors] = useState({})
  const [submitting, submit] = useSubmitting()

  const handleLinkClick = () => {
    dispatch(clearFlashNotice())
    dispatch(clearFlashAlert())
  }

  const handleSubmit = (evt) => {
    evt.preventDefault()
    setErrors({})
    dispatch(clearFlashNotice())
    dispatch(clearFlashAlert())

    const data = new FormData(evt.currentTarget)

    submit(async () => {
      const result = await deviseFetch(dispatch, '/user.json', {
        method: 'DELETE',
        body: data,
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': authToken,
        },
        credentials: 'same-origin',
      })

      if (result.failure) {
        showFailedRequest(dispatch, result)
        return
      }
      const { response, json: json_data } = result

      if (response.status === 204) {
        // The account is gone. Clear everything the app holds, as signing out
        // does, then say so on the welcome page. No await before the navigate,
        // or the flash would be cleared with it.
        dispatch(clearUserData())
        dispatch(clearPagesData())
        dispatch(clearStikisData())
        dispatch(clearSharesData())
        dispatch(setFlashNotice('Your account was deleted.'))
        navigate('/app/welcome')
        return
      }

      // A 422 is a missing or wrong password: show it under the field, with a
      // summary in the flash. Anything else gets the server's message.
      const { fieldErrors, flashMessage } = splitFieldErrors(
        json_data, ['current_password'], 'your account from being deleted'
      )
      setErrors(fieldErrors)
      dispatch(setFlashAlert(flashMessage))
    })
  }

  return (
    <React.Fragment>
      <Avatar sx={{ m: 1, bgcolor: 'error.main' }}>
        <DeleteForeverOutlinedIcon />
      </Avatar>
      <Typography component='h1' variant='h5'>
        Delete your account
      </Typography>
      <Box sx={{ mt: 2, width: '100%', maxWidth: 480 }}>
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          <AlertTitle>This cannot be undone</AlertTitle>
          Deleting the account <strong>{user?.login}</strong> also deletes every
          page you own and the stikis on them, and anyone you shared those pages
          with loses them too. Pages other people shared with you are not
          deleted; you simply lose access to them.
        </Alert>
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
        >
          <TextField
            margin="normal"
            required
            fullWidth
            label="Your password"
            id="user[current_password]"
            name="user[current_password]"
            type="password"
            autoComplete="current-password"
            error={Boolean(errors.current_password)}
            helperText={fieldErrorText(errors, 'current_password')}
            autoFocus
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="error"
            loading={submitting}
            sx={{ mt: 3, mb: 2 }}
          >
            Delete my account
          </Button>
          <Link
            component={RouterLink}
            onClick={handleLinkClick}
            to='/app/account'
            variant='body2'
            rel='nofollow'
          >
            No, keep my account
          </Link>
        </Box>
      </Box>
    </React.Fragment>
  )
}
